import { useNotification } from "@expert/common-ui";
import { useCallback, useEffect, useRef } from "react";

import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useActiveTask, useAgentSdk, useAgentStore, useLogger } from "@/sdk";

import { resolveNonRoutableTarget } from "./activityResolution";

export function useMorningPaperShiftRitual() {
    const sdk = useAgentSdk();
    const activity = useAgentStore((state) => state.activity);
    const activeTask = useActiveTask();
    const logger = useLogger({ module: "MorningPaperShiftRitual" });
    const partnerFeatures = usePartnerFeatureConfigured();
    const logOutOnOfflineAgentActivity = partnerFeatures?.logOutOnOfflineAgentActivity ?? false;
    const notification = useNotification();
    const hasAttemptedPark = useRef(false);
    const parkingPromise = useRef<Promise<void> | null>(null);

    useEffect(() => {
        if (hasAttemptedPark.current) return;
        if (!activity) return;
        if (activeTask) return;

        parkExpert();

        function parkExpert() {
            const target = resolveNonRoutableTarget(sdk, logOutOnOfflineAgentActivity);

            if (!target) {
                hasAttemptedPark.current = true;
                logger.warn(
                    {
                        defaultAgentActivity: sdk.defaultAgentActivity,
                        logOutOnOfflineAgentActivity,
                        agentActivities: sdk.agentActivities,
                    },
                    "Morning paper: no safe non-routable activity available; expert stays in prior activity",
                );
                return;
            }

            if (activity === target) {
                hasAttemptedPark.current = true;
                logger.info({ activity }, "Morning paper: expert already on non-routable activity; no change needed");
                return;
            }

            hasAttemptedPark.current = true;

            parkingPromise.current = sdk.setAgentActivity(target).then(
                () => {
                    logger.info(
                        { from: activity, to: target },
                        "Morning paper: parked expert in non-routable activity",
                    );
                },
                (err: unknown) => {
                    logger.error({ err, target }, "Morning paper: failed to park expert; expert stays routable");
                },
            );
        }
    }, [sdk, activity, activeTask, logger, logOutOnOfflineAgentActivity]);

    const startShift = useCallback(async (): Promise<void> => {
        // If a park is in-flight, wait for it to fully settle before unparking.
        // Sequential — not racing — so a slow park can't resolve *after* the unpark
        // and silently leave the agent stuck on Coaching/Break.
        if (parkingPromise.current) {
            await parkingPromise.current.catch(() => {});
        }

        try {
            await sdk.setAgentActivity(sdk.availableAgentActivity);
            logger.info("Morning paper: unparked expert to Available");
        } catch (err: unknown) {
            logger.error({ err }, "Morning paper: failed to unpark expert to Available");
            // Surface the failure so the agent doesn't silently sit on a non-routable
            // activity (e.g. Coaching) thinking they're available for calls.
            notification.error("Couldn't set your status to Available. Please set it manually from the status menu.");
        }
    }, [sdk, logger, notification]);

    return { startShift };
}
